import './styles/SystemNotificationItem.scss';

import { useMutation } from '@apollo/client';
import { BuildOutlined, ErrorOutlined, InfoOutlined, Menu } from '@mui/icons-material';
import { Chip, Fab, Grid, Icon, ListItemIcon, ListItemText, MenuItem, Paper } from '@mui/material';

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import DELETE_MUTATION from 'gql/mutations/MessagingService/deleteSystemNotification.gql';

import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { SystemNotificationModal } from 'app/containers/SystemNotifications/SystemNotification';
import { types } from 'app/containers/SystemNotifications/SystemNotification/NotificationTypes';
import { translations } from 'locales/i18n';
import { SytemNotification } from 'types/AppTypes';

import { LabeledInput } from '../../CompanyItem/components/LabeledInput';

interface Props {
  notification: SytemNotification;
}

export function SystemNotificationItem({ notification }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const anchorRef = useRef(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [deleteSystemNotification] = useMutation(DELETE_MUTATION);

  const handleDelete = async () => {
    try {
      await deleteSystemNotification({
        variables: {
          id: notification.id
        },
        refetchQueries: ['GetSystemNotifications']
      });
      dispatch(
        AlertActions.success(
          t(
            translations.pages.systemNotificationsSettings.alerts.success.deleteSystemNotification()
          )
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(
          t(translations.pages.systemNotificationsSettings.alerts.error.deleteSystemNotification())
        )
      );
    }
  };

  const TypeChip = () => {
    const label = types.find(type => type.value === notification.type)?.label;
    return (
      <Chip
        icon={getIcon()}
        className={`system-notification__type--${notification.type}`}
        label={label}
      />
    );
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'info':
        return <InfoOutlined className="info-box__header__icon" style={{ marginRight: '0' }} />;
      case 'maintenance':
        return <BuildOutlined className="info-box__header__icon" style={{ marginRight: '0' }} />;
      case 'incident':
        return <ErrorOutlined className="info-box__header__icon" style={{ marginRight: '0' }} />;
    }
  };

  const PeriodChips = () => {
    return (
      <Grid container>
        <Grid className="system-notification__period" item xs={12}>
          <LabeledInput label={t(translations.pages.systemNotificationsSettings.startDate())}>
            <Chip
              className="system-notification__period__chip"
              component={Paper}
              icon={
                <Icon
                  className="far fa-calendar-alt"
                  style={{
                    width: 'auto',
                    marginLeft: '0.5em',
                    marginRight: '0'
                  }}
                />
              }
              label={`${new Date(notification.startDate).toLocaleDateString('de-DE', {
                timeZone: 'Europe/Berlin'
              })} ${new Date(notification.startDate).toLocaleTimeString('de-DE', {
                timeZone: 'Europe/Berlin'
              })}`}
            />
          </LabeledInput>
        </Grid>
        {notification.endDate && (
          <Grid className="system-notification__period" item xs={12}>
            <LabeledInput label={t(translations.pages.systemNotificationsSettings.endDate())}>
              <Chip
                className="system-notification__period__chip"
                component={Paper}
                icon={
                  <Icon
                    className="far fa-calendar-alt"
                    style={{
                      width: 'auto',
                      marginLeft: '0.5em',
                      marginRight: '0'
                    }}
                  />
                }
                label={`${new Date(notification.endDate).toLocaleDateString('de-DE', {
                  timeZone: 'Europe/Berlin'
                })} ${new Date(notification.endDate).toLocaleTimeString('de-DE', {
                  timeZone: 'Europe/Berlin'
                })}`}
              />
            </LabeledInput>
          </Grid>
        )}
      </Grid>
    );
  };

  const ActionButtons = () => {
    return (
      <ContextMenu open={menuOpen} setOpen={setMenuOpen} anchorEl={anchorRef.current}>
        <MenuItem>
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setEditModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-edit"
                  style={{
                    width: 'auto',
                    color: 'lightblue'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.edit())} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem className="ioCourse-item__actions__container">
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setDeleteModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-trash-alt"
                  style={{
                    width: 'auto',
                    color: 'red'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.delete())} />
            </Grid>
          </Grid>
        </MenuItem>
      </ContextMenu>
    );
  };

  return (
    <>
      <ErrorBoundary boundaryTag="systemNotificationManagementBoundary">
        <Modal
          open={editModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setEditModalOpen(false);
          }}
          headline={t(translations.pages.systemNotificationsSettings.newSystemNotification())}
          onCancel={() => {
            setEditModalOpen(false);
          }}
          disabledCancel={disabledCancel}
        >
          <SystemNotificationModal
            systemNotification={notification}
            closeHandler={() => setEditModalOpen(false)}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.systemNotificationsSettings.deleteConfirm.title())}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          handleDelete();
          setDeleteModalOpen(false);
        }}
      >
        <div>{t(translations.pages.systemNotificationsSettings.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <Grid
        container
        className="system-notification"
        style={{
          backgroundColor: !notification.isActive ? 'lightgrey' : undefined
        }}
      >
        <Grid item xs={1} className="system-notification__id">
          {notification.id}
        </Grid>
        <Grid item xs={2} className="system-notification__type">
          <TypeChip />
        </Grid>
        <Grid item xs={6} className="system-notification__content">
          <Grid className="system-notification__content__headline" item>
            {notification.headline}
          </Grid>
          <Grid className="system-notification__content__content" item>
            <LabeledInput label={t(translations.pages.systemNotificationsSettings.content())}>
              <div style={{ whiteSpace: 'normal' }}>
                {notification.content.length > 126
                  ? notification.content.substring(0, 126) + '...'
                  : notification.content}
              </div>
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid item xs={2} className="system-notification__period">
          <PeriodChips />
        </Grid>
        <Grid item xs={1} className="system-notification__actions">
          <Fab size="small" className="action" onClick={() => setMenuOpen(true)} ref={anchorRef}>
            <Menu />
          </Fab>
          <ActionButtons />
        </Grid>
      </Grid>
    </>
  );
}
